import React, { Suspense } from 'react';
import { Count, Expression, Relation } from '@cotera/era';
import {
  useAction,
  useInScopeModel,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { GraphModel } from '../graph/graph.model';
import { useArtifactQuery } from '@cotera/client/app/etc/data/duckdb';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Icon } from '@cotera/client/app/components/ui';
import { Button } from '../components/ui/button';
import { SetPrimaryKeyAction } from '../dataset/actions/set-pk.action';
import { Assert } from '@cotera/utilities';
import { ErrorBoundary } from 'react-error-boundary';

type PkUniquenessWarningProps = {
  relName: string;
  baseRel: Relation;
};

export const PkUniquenessWarning: React.FC<PkUniquenessWarningProps> = ({
  relName,
  baseRel,
}) => {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <PkUniquenessWarningInner relName={relName} baseRel={baseRel} />
      </Suspense>
    </ErrorBoundary>
  );
};

const PkUniquenessWarningInner: React.FC<PkUniquenessWarningProps> = ({
  relName,
  baseRel,
}) => {
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const graph = useWatchableValue(graphModel.graph);

  const relConfig = graph.getRel({ symbol: relName });

  if (relConfig?.builtNode.isErr() || !relConfig?.builtNode.isOk()) {
    return null;
  }

  const relItem = relConfig.builtNode.value.item;

  if (relItem.t !== 'rel') {
    return null;
  }

  const pkExpression = relItem.pk;

  if (pkExpression === null) {
    return null;
  }

  return <View baseRel={baseRel} pkExpression={pkExpression} />;
};

const View: React.FC<{ baseRel: Relation; pkExpression: Expression }> = ({
  baseRel,
  pkExpression,
}) => {
  const setPk = useAction(SetPrimaryKeyAction);

  // Step 2: Count occurrences of each PK value using countBy
  // Step 3: Filter to only rows where COUNT > 1 (duplicates)
  // Step 4: Use summary to check if there are ANY duplicates
  const countRel = baseRel
    .groupBy((_) => ({ __pk_check: pkExpression }))
    .select((t) => ({
      ...t.group(),
      COUNT: Count(),
    }))
    .where((t) => t.attr('COUNT').gt(1));

  const { data } = useArtifactQuery({
    baseRel: countRel,
    rel: (rel) => rel,
  });

  const result = data.data.row(0);

  if (result === undefined) {
    return null;
  }

  const hasDuplicates = result.column('COUNT').valueAt(0);

  if (hasDuplicates === null || hasDuplicates === undefined) {
    return null;
  }

  if (
    hasDuplicates === false ||
    hasDuplicates === 0 ||
    hasDuplicates === false
  ) {
    return null;
  }

  return (
    <Alert variant="warning" className="mb-4">
      <Icon icon="warning" />
      <AlertTitle>Primary Key Not Unique</AlertTitle>
      <AlertDescription className="flex flex-col gap-4">
        <p>
          The primary key column contains duplicate values. This will mean you
          are unable to run columns or enrichments on this dataset. Consider
          updating your primary key to ensure uniqueness.
        </p>
        <Button
          className="w-fit"
          variant="outline"
          onClick={async () => {
            await setPk.execute();
          }}
        >
          Update Primary Key
        </Button>
      </AlertDescription>
    </Alert>
  );
};
